import { Component, computed, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NumberSuffixPipe } from '@core/pipes/number-suffix.pipe';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { EKpiGoalsStatusBadge } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/cta-analytics-details-kpi-goals-table/cta-analytics-details-kpi-goals-table.interface';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import { EEventViewMode, TEventFrontendTab } from '@enums/event-tracking.enum';
import { IPlatformInformationApi } from '@interfaces/campaign-analytics-api.interface';
import { TranslateModule } from '@ngx-translate/core';
import { ICreatorCampaignExport, TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { TTileContentStatus } from '@shared/components/social-content-tile/social-content-tile.interface';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime } from 'rxjs/operators';
import {
  IDeepLinkContentsEvent,
  IDeepLinkCreatorInfo,
  IDeepLinkKpiGoalsEvent,
  TDeepLinkEvent,
} from '../../campaign-details-deep-link/campaign-details-deep-link.interface';
import { ECampaignDetailsTab, ICampaignDetailsCreator } from '../../campaign-details.interface';
import { CreatorActionsMenuComponent } from '../creator-actions-menu/creator-actions-menu.component';

export interface ICreatorStatusPopoverRow {
  colorClass: string;
  label: string;
  count: number;
  clickFn: () => void;
}

@Component({
  imports: [
    CreatorActionsMenuComponent,
    IconBadgeComponent,
    IconComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
    NumberSuffixPipe,
    ReactiveFormsModule,
    SocialBadgeComponent,
    TranslateModule,
  ],
  selector: 'app-campaign-details-creators-grid',
  styleUrl: './campaign-details-creators-grid.component.scss',
  templateUrl: './campaign-details-creators-grid.component.html',
})
export class CampaignDetailsCreatorsGridComponent {
  public readonly creators = input.required<ICampaignDetailsCreator[]>();
  public readonly campaign = input.required<TCampaignApi | null>();
  public readonly creatorCampaignExports = input<Map<string, ICreatorCampaignExport>>(new Map());
  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly exportMode = input<boolean>(false);
  public readonly loading = input<boolean>(false);
  public readonly invitedCreatorIds = input<Set<string>>(new Set());

  public readonly deepLinkClicked = output<TDeepLinkEvent>();

  // This component only ever renders for the creators tab's "grid" view - a literal constant
  // rather than an input, since that's structural (the parent only instantiates this component
  // when its view mode is grid).
  public readonly viewMode = EEventViewMode.tiles;
  // Briefing events fired from this tab always report the creators tab as current (structural -
  // this component only renders inside the creators tab).
  public readonly currentTab: TEventFrontendTab = toEventFrontendTab(ECampaignDetailsTab.creators);

  public readonly EAnalyticsStatus = EAnalyticsStatus;

  public readonly searchControl = new FormControl<string>('');
  public readonly searchQuery = signal<string>('');

  public readonly filteredCreators = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();

    if (!query) {
      return this.creators();
    }

    return this.creators().filter((c) => c.name.toLowerCase().includes(query));
  });

  public readonly campaignCreatorsMap = computed(() => {
    return new Map(this.campaign()?.campaignCreators?.map((cc) => [cc.creatorId, cc]) ?? []);
  });

  constructor() {
    this.searchControl.valueChanges.pipe(debounceTime(300), takeUntilDestroyed()).subscribe((value) => {
      this.searchQuery.set(value ?? '');
    });
  }

  public generateSocialUrl(usedPlatform: IPlatformInformationApi): string {
    return SocialUtilitiesService.generateSocialUrl(usedPlatform.platform, usedPlatform.platformId);
  }

  public getUsedPlatformTracking(usedPlatform: IPlatformInformationApi): string {
    return `${usedPlatform.platformId}-${usedPlatform.platform}`;
  }

  public getStatusDotClass(status: EAnalyticsStatus): string {
    switch (status) {
      case EAnalyticsStatus.ok:
        return 'bg-shikenso-success-base';
      case EAnalyticsStatus.problem:
        return 'bg-shikenso-warning-base';
      case EAnalyticsStatus.failed:
        return 'bg-shikenso-error-bright';
      case EAnalyticsStatus.processing:
        return 'bg-shikenso-purple-base-60';
    }
  }

  public getStatusLabel(status: EAnalyticsStatus): string {
    switch (status) {
      case EAnalyticsStatus.ok:
        return 'pages.campaignDetails.creators.filters.allGood';
      case EAnalyticsStatus.problem:
        return 'pages.campaignDetails.creators.filters.attention';
      case EAnalyticsStatus.failed:
        return 'pages.campaignDetails.creators.filters.critical';
      case EAnalyticsStatus.processing:
        return 'pages.campaignDetails.creators.filters.notStarted';
    }
  }

  public hasIssues(creator: ICampaignDetailsCreator): boolean {
    return creator.mergedStatus === EAnalyticsStatus.failed || creator.mergedStatus === EAnalyticsStatus.problem;
  }

  public getPopoverRows(creator: ICampaignDetailsCreator): ICreatorStatusPopoverRow[] {
    const kpi = creator.ctaKpiStatus;
    const rows: ICreatorStatusPopoverRow[] = [];

    if (creator.mergedStatus === EAnalyticsStatus.ok || creator.mergedStatus === EAnalyticsStatus.processing) {
      return [];
    }

    // Red rows first (missing + failed contents, failed KPIs)
    if (kpi.currentMissingContents > 0) {
      rows.push({
        clickFn: () => this.emitMissingContentsDeepLink(creator),
        colorClass: 'bg-shikenso-error-bright',
        count: kpi.currentMissingContents,
        label: 'pages.campaignDetails.overview.missingContents',
      });
    }

    if (kpi.currentFailedContents > 0) {
      rows.push({
        clickFn: () => this.emitFailedContentsDeepLink(creator),
        colorClass: 'bg-shikenso-error-bright',
        count: kpi.currentFailedContents,
        label: 'pages.campaignDetails.overview.failedContents',
      });
    }

    if (kpi.currentFailedKpis > 0) {
      rows.push({
        clickFn: () => this.emitGoalsUnavailableDeepLink(creator),
        colorClass: 'bg-shikenso-error-bright',
        count: kpi.currentFailedKpis,
        label: 'pages.campaignDetails.overview.goalsUnavailable',
      });
    }

    // Orange rows (problem contents, problem KPIs)
    if (kpi.currentProblemContents > 0) {
      rows.push({
        clickFn: () => this.emitProblemContentsDeepLink(creator),
        colorClass: 'bg-shikenso-warning-base',
        count: kpi.currentProblemContents,
        label: 'pages.campaignDetails.overview.problemContents',
      });
    }

    if (kpi.currentProblemKpis > 0) {
      rows.push({
        clickFn: () => this.emitGoalsBelowTargetDeepLink(creator),
        colorClass: 'bg-shikenso-warning-base',
        count: kpi.currentProblemKpis,
        label: 'pages.campaignDetails.overview.goalsBelowTarget',
      });
    }

    return rows;
  }

  public getIssueCount(creator: ICampaignDetailsCreator): number {
    const kpi = creator.ctaKpiStatus;

    if (creator.mergedStatus === EAnalyticsStatus.failed) {
      return kpi.currentFailedKpis + kpi.currentFailedContents + kpi.currentMissingContents;
    }

    if (creator.mergedStatus === EAnalyticsStatus.problem) {
      return kpi.currentProblemKpis + kpi.currentProblemContents;
    }

    return 0;
  }

  public onViewCreator(creator: ICampaignDetailsCreator, event: Event): void {
    event.stopPropagation();
    WindowRedirectService.openUrlInNewTab(`/creators/${creator.id}`);
  }

  public onPopoverRowClick(row: ICreatorStatusPopoverRow, event: Event): void {
    event.stopPropagation();
    row.clickFn();
  }

  public onAllGoodRowClick(creator: ICampaignDetailsCreator, event: Event): void {
    event.stopPropagation();
    this.deepLinkClicked.emit({
      contentStatuses: new Set<TTileContentStatus>(['fulfilled']),
      creator: { icon: creator.icon, id: creator.id, name: creator.name },
      matchingTypes: new Set([EMatchingType.matched]),
      tab: ECampaignDetailsTab.contents,
    } as IDeepLinkContentsEvent);
  }

  public onNotStartedRowClick(creator: ICampaignDetailsCreator, event: Event): void {
    event.stopPropagation();
    this.deepLinkClicked.emit({
      creator: { icon: creator.icon, id: creator.id, name: creator.name },
      matchingTypes: new Set([EMatchingType.planned]),
      tab: ECampaignDetailsTab.contents,
    } as IDeepLinkContentsEvent);
  }

  public onMissingContentsRowClick(creator: ICampaignDetailsCreator, event: Event): void {
    event.stopPropagation();
    this.emitMissingContentsDeepLink(creator);
  }

  private creatorInfo(creator: ICampaignDetailsCreator): IDeepLinkCreatorInfo {
    return { icon: creator.icon, id: creator.id, name: creator.name };
  }

  private emitMissingContentsDeepLink(creator: ICampaignDetailsCreator): void {
    this.deepLinkClicked.emit({
      creator: this.creatorInfo(creator),
      matchingTypes: new Set([EMatchingType.missing]),
      tab: ECampaignDetailsTab.contents,
    } as IDeepLinkContentsEvent);
  }

  private emitFailedContentsDeepLink(creator: ICampaignDetailsCreator): void {
    this.deepLinkClicked.emit({
      contentStatuses: new Set<TTileContentStatus>(['notFulfilled']),
      creator: this.creatorInfo(creator),
      matchingTypes: new Set([EMatchingType.matched]),
      tab: ECampaignDetailsTab.contents,
    } as IDeepLinkContentsEvent);
  }

  private emitProblemContentsDeepLink(creator: ICampaignDetailsCreator): void {
    this.deepLinkClicked.emit({
      contentStatuses: new Set<TTileContentStatus>(['partiallyFulfilled']),
      creator: this.creatorInfo(creator),
      matchingTypes: new Set([EMatchingType.matched]),
      tab: ECampaignDetailsTab.contents,
    } as IDeepLinkContentsEvent);
  }

  private emitGoalsBelowTargetDeepLink(creator: ICampaignDetailsCreator): void {
    this.deepLinkClicked.emit({
      creator: this.creatorInfo(creator),
      statuses: new Set([EKpiGoalsStatusBadge.kpiGoalsBelowTarget]),
      tab: ECampaignDetailsTab.kpiGoals,
    } as IDeepLinkKpiGoalsEvent);
  }

  private emitGoalsUnavailableDeepLink(creator: ICampaignDetailsCreator): void {
    this.deepLinkClicked.emit({
      creator: this.creatorInfo(creator),
      statuses: new Set([EKpiGoalsStatusBadge.kpiGoalsUnavailable]),
      tab: ECampaignDetailsTab.kpiGoals,
    } as IDeepLinkKpiGoalsEvent);
  }
}
